import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { GameComponentProps } from '../core/types';
import { GridCell } from '../../shared/grid/GridCell';
import { HintButton } from '../../shared/grid/HintButton';
import { NumberPad } from '../../shared/grid/NumberPad';
import { UndoButton } from '../../shared/grid/UndoButton';
import { isGridArrowKey, moveGridSelection } from '../../shared/grid/navigation';
import { findConflictIndices, placementWarning } from './logic';
import {
  isMiniSudokuPuzzle,
  SUDOKU_DIGITS,
  SUDOKU_SIZE,
  type MiniSudokuPuzzle,
  type SudokuDigit,
  type SudokuValue,
} from './types';

type SudokuMove = {
  index: number;
  before: SudokuValue;
  after: SudokuValue;
  selectionBefore: number;
};

type MiniSudokuSavedState = {
  version: 1;
  board: SudokuValue[];
  selectedIndex: number;
  history: SudokuMove[];
  hintUsed: boolean;
  revealedWrong: number[];
};

function readSavedState(value: unknown, puzzle: MiniSudokuPuzzle): MiniSudokuSavedState {
  const firstEmpty = puzzle.puzzle.findIndex((cell) => cell === 0);
  const fallback: MiniSudokuSavedState = {
    version: 1,
    board: [...puzzle.puzzle],
    selectedIndex: firstEmpty >= 0 ? firstEmpty : 0,
    history: [],
    hintUsed: false,
    revealedWrong: [],
  };
  if (!value || typeof value !== 'object') return fallback;
  const candidate = value as Partial<MiniSudokuSavedState>;
  const board =
    Array.isArray(candidate.board) && candidate.board.length === 36
      ? candidate.board.map((cell, index) => {
          if (puzzle.puzzle[index] !== 0) return puzzle.puzzle[index];
          return Number.isInteger(cell) && cell! >= 0 && cell! <= 6 ? (cell as SudokuValue) : 0;
        })
      : fallback.board;
  const selectedIndex =
    Number.isInteger(candidate.selectedIndex) &&
    candidate.selectedIndex! >= 0 &&
    candidate.selectedIndex! < 36
      ? candidate.selectedIndex!
      : fallback.selectedIndex;
  const history = Array.isArray(candidate.history)
    ? candidate.history.filter(isSudokuMove).filter((move) => puzzle.puzzle[move.index] === 0)
    : [];
  const revealedWrong = Array.isArray(candidate.revealedWrong)
    ? candidate.revealedWrong.filter(
        (index): index is number =>
          Number.isInteger(index) && index >= 0 && index < 36 && puzzle.puzzle[index] === 0,
      )
    : [];
  return {
    version: 1,
    board,
    selectedIndex,
    history,
    hintUsed: candidate.hintUsed === true || revealedWrong.length > 0,
    revealedWrong,
  };
}

function isSudokuMove(value: unknown): value is SudokuMove {
  if (!value || typeof value !== 'object') return false;
  const move = value as Partial<SudokuMove>;
  return (
    Number.isInteger(move.index) &&
    move.index! >= 0 &&
    move.index! < 36 &&
    Number.isInteger(move.before) &&
    move.before! >= 0 &&
    move.before! <= 6 &&
    Number.isInteger(move.after) &&
    move.after! >= 0 &&
    move.after! <= 6 &&
    Number.isInteger(move.selectionBefore) &&
    move.selectionBefore! >= 0 &&
    move.selectionBefore! < 36
  );
}

export function MiniSudokuGame({
  puzzle,
  savedState,
  disabled,
  onStateChange,
  onCharge,
  onComplete,
}: GameComponentProps) {
  if (!isMiniSudokuPuzzle(puzzle.payload)) {
    throw new Error('Mini Sudoku received an invalid puzzle payload.');
  }
  const sudoku = puzzle.payload;
  const initial = useMemo(() => readSavedState(savedState, sudoku), [savedState, sudoku]);
  const [board, setBoard] = useState(initial.board);
  const [selectedIndex, setSelectedIndex] = useState(initial.selectedIndex);
  const [history, setHistory] = useState(initial.history);
  const [hintUsed, setHintUsed] = useState(initial.hintUsed);
  const [revealedWrong, setRevealedWrong] = useState(new Set(initial.revealedWrong));
  const [status, setStatus] = useState('');
  const gridRef = useRef<HTMLDivElement>(null);
  const completionSent = useRef(false);

  const conflicts = useMemo(() => findConflictIndices(board), [board]);
  const selectedBoxValues = useMemo(() => {
    const selectedRow = Math.floor(selectedIndex / SUDOKU_SIZE);
    const selectedColumn = selectedIndex % SUDOKU_SIZE;
    const boxRow = Math.floor(selectedRow / 2);
    const boxColumn = Math.floor(selectedColumn / 3);
    return [
      ...new Set(
        board.filter((value, index) => {
          if (value === 0) return false;
          const row = Math.floor(index / SUDOKU_SIZE);
          const column = index % SUDOKU_SIZE;
          return Math.floor(row / 2) === boxRow && Math.floor(column / 3) === boxColumn;
        }),
      ),
    ];
  }, [board, selectedIndex]);

  const persist = useCallback(
    (
      nextBoard: SudokuValue[],
      nextSelected: number,
      nextHistory: SudokuMove[],
      nextHintUsed: boolean,
      nextWrong: Set<number>,
    ) => {
      onStateChange({
        version: 1,
        board: nextBoard,
        selectedIndex: nextSelected,
        history: nextHistory,
        hintUsed: nextHintUsed,
        revealedWrong: [...nextWrong],
      });
    },
    [onStateChange],
  );

  const select = useCallback(
    (index: number, focus = false) => {
      setSelectedIndex(index);
      persist(board, index, history, hintUsed, revealedWrong);
      if (focus) {
        window.requestAnimationFrame(() => {
          gridRef.current?.querySelector<HTMLElement>(`[data-grid-index="${index}"]`)?.focus();
        });
      }
    },
    [board, hintUsed, history, persist, revealedWrong],
  );

  const enterValue = useCallback(
    (value: SudokuValue) => {
      if (disabled || sudoku.puzzle[selectedIndex] !== 0 || board[selectedIndex] === value) return;
      const warning = value === 0 ? null : placementWarning(board, selectedIndex, value);
      const nextBoard = [...board];
      const move: SudokuMove = {
        index: selectedIndex,
        before: board[selectedIndex],
        after: value,
        selectionBefore: selectedIndex,
      };
      nextBoard[selectedIndex] = value;
      const nextHistory = [...history, move];
      const nextWrong = new Set(revealedWrong);
      nextWrong.delete(selectedIndex);
      setBoard(nextBoard);
      setHistory(nextHistory);
      setRevealedWrong(nextWrong);
      setStatus(warning ?? (value === 0 ? 'Cell cleared' : `${value} entered`));
      persist(nextBoard, selectedIndex, nextHistory, hintUsed, nextWrong);
    },
    [board, disabled, hintUsed, history, persist, revealedWrong, selectedIndex, sudoku.puzzle],
  );

  const undo = useCallback(() => {
    if (disabled || history.length === 0) return;
    const move = history.at(-1)!;
    const nextHistory = history.slice(0, -1);
    const nextBoard = [...board];
    nextBoard[move.index] = move.before;
    const nextWrong = new Set(revealedWrong);
    nextWrong.delete(move.index);
    setBoard(nextBoard);
    setSelectedIndex(move.selectionBefore);
    setHistory(nextHistory);
    setRevealedWrong(nextWrong);
    setStatus('Move undone');
    persist(nextBoard, move.selectionBefore, nextHistory, hintUsed, nextWrong);
  }, [board, disabled, hintUsed, history, persist, revealedWrong]);

  const revealWrong = useCallback(() => {
    if (disabled) return;
    const nextWrong = new Set<number>();
    board.forEach((value, index) => {
      if (sudoku.puzzle[index] === 0 && value !== 0 && value !== sudoku.solution[index]) {
        nextWrong.add(index);
      }
    });
    if (!hintUsed) onCharge('reveal-wrong', 30_000, 'Reveal wrong cells');
    setHintUsed(true);
    setRevealedWrong(nextWrong);
    setStatus(
      `${nextWrong.size} wrong ${nextWrong.size === 1 ? 'cell' : 'cells'} found${hintUsed ? '' : ' · +30s'}`,
    );
    persist(board, selectedIndex, history, true, nextWrong);
  }, [board, disabled, hintUsed, history, onCharge, persist, selectedIndex, sudoku]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (disabled || event.metaKey || event.ctrlKey || event.altKey) return;
      if (/^[1-6]$/.test(event.key)) {
        event.preventDefault();
        enterValue(Number(event.key) as SudokuDigit);
      } else if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault();
        enterValue(0);
      } else if (isGridArrowKey(event.key)) {
        event.preventDefault();
        select(moveGridSelection(selectedIndex, event.key, 6, 6), true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [disabled, enterValue, select, selectedIndex]);

  useEffect(() => {
    if (completionSent.current || !board.every((value, index) => value === sudoku.solution[index]))
      return;
    completionSent.current = true;
    onComplete({
      kind: 'solved',
      baseDeltaMs: hintUsed ? 0 : -10_000,
      message: 'Perfect grid!',
      metadata: { hintUsed, moveCount: history.length },
    });
  }, [board, hintUsed, history.length, onComplete, sudoku.solution]);

  return (
    <section className="mini-sudoku-game" aria-labelledby="mini-sudoku-title">
      <p className="eyebrow">6 × 6 · boxes 2 × 3</p>
      <h1 id="mini-sudoku-title" className="sr-only">
        Mini Sudoku
      </h1>
      <div
        ref={gridRef}
        className="sudoku-grid"
        role="grid"
        aria-label="Mini Sudoku grid, 6 rows by 6 columns with 2 by 3 boxes"
      >
        {board.map((value, index) => {
          const row = Math.floor(index / SUDOKU_SIZE);
          const column = index % SUDOKU_SIZE;
          const given = sudoku.puzzle[index] !== 0;
          const conflict = conflicts.has(index);
          const wrong = revealedWrong.has(index);
          return (
            <GridCell
              key={index}
              index={index}
              label={`Row ${row + 1} column ${column + 1}, ${value || 'empty'}, ${given ? 'given' : 'editable'}${
                conflict ? ', conflict' : ''
              }${wrong ? ', revealed wrong' : ''}`}
              selected={selectedIndex === index}
              given={given}
              conflict={conflict}
              wrong={wrong}
              className={`sudoku-cell sudoku-cell--row-${row} sudoku-cell--column-${column}`}
              onSelect={select}
            >
              <span>{value || ''}</span>
              {(conflict || wrong) && <i aria-hidden="true">!</i>}
            </GridCell>
          );
        })}
      </div>

      <div className="sudoku-status" role="status" aria-live="polite">
        {status}
      </div>
      <NumberPad
        values={SUDOKU_DIGITS}
        mutedValues={selectedBoxValues}
        disabled={disabled || sudoku.puzzle[selectedIndex] !== 0}
        onValue={(value) => enterValue(value as SudokuDigit)}
        onErase={() => enterValue(0)}
      />
      <div className="grid-actions">
        <UndoButton count={history.length} disabled={disabled} onUndo={undo} />
        <HintButton
          label="Reveal wrong"
          costSeconds={30}
          paid={hintUsed}
          disabled={disabled}
          onClick={revealWrong}
        />
      </div>
    </section>
  );
}
